import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import AdminSidebar from "../components/layout/AdminSidebar";
import { 
  Bot,
  ArrowLeft,
  Send,
  FileText,
  AlertTriangle,
  ClipboardCheck,
  MessageSquare,
  BookOpen,
  Sparkles,
  AlertCircle
} from "lucide-react";

const AIAssistant = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [contextType, setContextType] = useState("general");
  const [includeColoradoLaw, setIncludeColoradoLaw] = useState(true);
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [draftType, setDraftType] = useState("");
  const [draftContext, setDraftContext] = useState("");
  const [draftLoading, setDraftLoading] = useState(false);
  const [draftResponse, setDraftResponse] = useState(null);
  const [activeTab, setActiveTab] = useState("analyze");

  const handleAnalyze = async () => {
    if (!query.trim()) {
      toast.error("Please enter a question");
      return;
    }

    setLoading(true);
    setResponse(null);
    try {
      const res = await axios.post(`${API}/ai/analyze`, {
        query,
        context_type: contextType,
        include_colorado_law: includeColoradoLaw
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResponse(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "AI analysis failed");
    } finally {
      setLoading(false);
    }
  };

  const handleDraft = async () => {
    if (!draftType || !draftContext.trim()) {
      toast.error("Please select a draft type and provide context");
      return;
    }

    setDraftLoading(true);
    setDraftResponse(null);
    try {
      const res = await axios.post(`${API}/ai/draft?draft_type=${draftType}&context=${encodeURIComponent(draftContext)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDraftResponse(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "AI drafting failed");
    } finally {
      setDraftLoading(false);
    }
  };

  const contextOptions = [
    { value: "general", label: "General HOA", icon: <BookOpen className="w-4 h-4" /> },
    { value: "violation", label: "Notices", icon: <AlertTriangle className="w-4 h-4" /> },
    { value: "drc", label: "Design Review", icon: <ClipboardCheck className="w-4 h-4" /> },
    { value: "correspondence", label: "Correspondence", icon: <MessageSquare className="w-4 h-4" /> }
  ];

  const draftOptions = [
    { value: "violation_notice", label: "Notice Letter" },
    { value: "drc_letter", label: "DRC Letter" },
    { value: "correspondence", label: "Owner Correspondence" },
    { value: "minutes", label: "Meeting Minutes" }
  ];

  const pageContent = (
    <div className="p-6 lg:p-8 max-w-4xl mx-auto">
      {/* Page Header */}
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <Bot className="w-5 h-5 text-primary" />
        </div>
        <div>
          <span className="font-serif text-lg font-semibold block">AI Assistant</span>
          <span className="text-xs text-muted-foreground">Get AI-powered help with HOA questions</span>
        </div>
      </div>

      {/* Info Banner */}
      <Card className="mb-6 border-primary/30 bg-primary/5">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <Sparkles className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-semibold mb-1">AI-Powered HOA Assistant</h3>
              <p className="text-sm text-muted-foreground">
                Ask questions about HOA rules, Colorado law, and get AI-generated drafts 
                for notices, DRC letters, and correspondence. All responses 
                include citations and require human review before use.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tab Navigation */}
      <div className="flex gap-2 mb-6">
        <Button
          variant={activeTab === "analyze" ? "default" : "outline"}
          onClick={() => setActiveTab("analyze")}
          data-testid="tab-analyze"
        >
            <BookOpen className="w-4 h-4 mr-2" />
            Analyze & Answer
          </Button>
          <Button
            variant={activeTab === "draft" ? "default" : "outline"}
            onClick={() => setActiveTab("draft")}
            data-testid="tab-draft"
          >
            <FileText className="w-4 h-4 mr-2" />
            Draft Documents
          </Button>
        </div>

        {/* Analyze Tab */}
        {activeTab === "analyze" && (
          <Card>
            <CardHeader>
              <CardTitle className="font-serif">Ask a Question</CardTitle>
              <CardDescription>
                Get AI-powered answers with citations from HOA documents and Colorado law
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Context Type</Label>
                <div className="flex flex-wrap gap-2">
                  {contextOptions.map((opt) => (
                    <Button
                      key={opt.value}
                      variant={contextType === opt.value ? "default" : "outline"}
                      size="sm"
                      onClick={() => setContextType(opt.value)}
                      data-testid={`context-${opt.value}`}
                    >
                      {opt.icon}
                      <span className="ml-2">{opt.label}</span>
                    </Button>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  checked={includeColoradoLaw}
                  onCheckedChange={setIncludeColoradoLaw}
                  data-testid="include-colorado-law"
                />
                <Label>Include Colorado HOA/Metro District Law</Label>
              </div>

              <div className="space-y-2">
                <Label>Your Question</Label>
                <Textarea
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="e.g., What are the rules about fence height in our community?"
                  rows={4}
                  data-testid="ai-query-input"
                />
              </div>

              <Button 
                onClick={handleAnalyze} 
                disabled={loading}
                className="w-full"
                data-testid="ai-analyze-btn"
              >
                {loading ? (
                  <>Analyzing...</>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Get Answer
                  </>
                )}
              </Button>

              {response && (
                <div className="mt-6 space-y-4">
                  <div className="p-4 bg-muted rounded-lg">
                    <h4 className="font-semibold mb-2">AI Response</h4>
                    <div className="prose prose-sm max-w-none text-foreground whitespace-pre-wrap">
                      {response.response}
                    </div>
                  </div>
                  
                  {response.citations?.length > 0 && (
                    <div className="p-4 bg-primary/5 rounded-lg">
                      <h4 className="font-semibold mb-2 text-sm">Citations</h4>
                      <ul className="text-sm space-y-1">
                        {response.citations.map((citation, i) => (
                          <li key={i} className="text-muted-foreground">
                            • {citation.source}: {citation.section}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="flex items-start gap-2 p-3 bg-yellow-500/10 rounded-lg text-yellow-700 dark:text-yellow-400">
                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <p className="text-xs">{response.disclaimer}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Draft Tab */}
        {activeTab === "draft" && (
          <Card>
            <CardHeader>
              <CardTitle className="font-serif">Draft Documents</CardTitle>
              <CardDescription>
                Generate AI-drafted letters, notices, and correspondence
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Document Type</Label>
                <Select value={draftType} onValueChange={setDraftType}>
                  <SelectTrigger data-testid="draft-type-select">
                    <SelectValue placeholder="Select document type" />
                  </SelectTrigger>
                  <SelectContent>
                    {draftOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Context & Details</Label>
                <Textarea
                  value={draftContext}
                  onChange={(e) => setDraftContext(e.target.value)}
                  placeholder="Provide details for the draft. For notices: describe the issue, property, dates. For DRC: project type, decision, conditions. For correspondence: topic, tone, key points."
                  rows={5}
                  data-testid="draft-context-input"
                />
              </div>

              <Button 
                onClick={handleDraft} 
                disabled={draftLoading}
                className="w-full"
                data-testid="ai-draft-btn"
              >
                {draftLoading ? (
                  <>Generating Draft...</>
                ) : (
                  <>
                    <FileText className="w-4 h-4 mr-2" />
                    Generate Draft
                  </>
                )}
              </Button>

              {draftResponse && (
                <div className="mt-6 space-y-4">
                  <div className="p-4 bg-muted rounded-lg">
                    <h4 className="font-semibold mb-2">Generated Draft</h4>
                    <div className="prose prose-sm max-w-none text-foreground whitespace-pre-wrap font-mono text-sm">
                      {draftResponse.response}
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-2 p-3 bg-yellow-500/10 rounded-lg text-yellow-700 dark:text-yellow-400">
                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <p className="text-xs">{draftResponse.disclaimer}</p>
                  </div>

                  <Button variant="outline" className="w-full" onClick={() => {
                    navigator.clipboard.writeText(draftResponse.response);
                    toast.success("Draft copied to clipboard");
                  }}>
                    Copy to Clipboard
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}
    </div>
  );

  return <AdminSidebar>{pageContent}</AdminSidebar>;
};

export default AIAssistant;
